// src/components/desk/contract/RentalPaymentPlan.jsx — the lease's money, as three questions
//
// Owner 2026-09-17: "This flow is really ambiguous. Just take whole form and analyze from top to
// bottom… Be human being for a moment." The v8 lease stores its payment arrangement as two stages
// of ~20 fields. They used to be asked one by one across three groups — the account picker before
// the question of who receives the money, a monthly-rent recipient dropdown with ONE option, and a
// monthly method silently defaulted to a bank transfer, so choosing "cash" ended at an IBAN nobody
// had mentioned.
//
// Now: (A) what is paid at signing stays where it was — the prepayment list, the deposit and any
// earlier payment, together; (B) WHO takes that money and HOW, as one choice; (C) how monthly rent
// is paid, "the same way" by default. Completion rules and processing days are derived
// (src/lib/rentalPaymentRoutes.js). The stored fields are unchanged, so the server rules and the
// printed contract are exactly what they were.
//
// ⚠️ Every follow-up question is drawn through the composer's own `renderRow`, so labels, the
//    "automatic" tag, suggestions, overrides and error marks behave like every other row.
import { useEffect } from 'react'
import { t } from '../../../lib/i18n.js'
import { routesForMethod } from '../../../lib/rentalOpening.js'
import { RentalOpeningSummary } from './RentalOpening.jsx'
import {
  SIGNING_ROUTES, TRANSFER_KINDS, signingRouteOf, signingSteps, signingRouteAllowed,
  monthlyRouteOf, monthlySteps, monthlySameAvailable, transfersAllowed, inactiveFeeSteps,
} from '../../../lib/rentalPaymentRoutes.js'

const CHOICE = 'w-full text-left rounded border px-2.5 py-1.5 text-[13px] leading-snug transition-colors'
const ON = 'border-[#1F6F4F] bg-[#1F6F4F]/10 text-[#1F6F4F]'
const OFF = 'border-[#B9B6AD] text-[#14130F] hover:border-[#14130F]/40'

// ✍️ HOW THE LEASE IS SIGNED — its own block, before the term and the money (owner 2026-09-23: "separate
// remote signing and key handover"): in person or remote, whether the tenant saw the flat, and — for a remote
// lease — the deadline by which the initial payment must arrive for the lease to take effect.
export function RentalSigningBlock({ values, fieldsByKey, renderRow, opening = null, openingQuestionsList = [], onChangeOpening = () => {} }) {
  const remote = values.remote_transaction === true
  const row = key => (fieldsByKey[key] ? renderRow(fieldsByKey[key]) : null)
  return (
    <section className="space-y-2.5" data-field="rental_signing" data-rental-signing>
      <div className="text-[13px] font-medium">{t('desk_contract_block_signing')}</div>
      {opening ? <RentalOpeningSummary answers={opening} questions={openingQuestionsList} onChange={onChangeOpening} only={['inspection']} />
        : <>{row('remote_transaction')}{remote && row('tenant_inspected_in_person')}</>}
      {remote && row('initial_payment_deadline')}
    </section>
  )
}

// 🔑 KEY HANDOVER — its own block, after the payment: when the keys are handed over, how much notice the
// tenant gets, and (remote) how long they have to report defects after moving in.
export function RentalHandoverBlock({ values, fieldsByKey, renderRow, opening = null, openingQuestionsList = [], onChangeOpening = () => {} }) {
  const remote = values.remote_transaction === true
  const later = values.key_handover_timing === 'later_by_arrangement'
  const row = key => (fieldsByKey[key] ? renderRow(fieldsByKey[key]) : null)
  const summary = opening && openingQuestionsList.includes('handover')
  if (!fieldsByKey.key_handover_timing && !remote) return null
  return (
    <section className="space-y-2.5" data-field="rental_handover" data-rental-handover>
      <div className="text-[13px] font-medium">{t('desk_contract_block_handover')}</div>
      {summary ? <RentalOpeningSummary answers={opening} questions={openingQuestionsList} onChange={onChangeOpening} only={['handover']} /> : row('key_handover_timing')}
      {(remote || later) && row('key_handover_notice_hours')}
      {remote && row('defect_report_hours')}
    </section>
  )
}

export function RentalPaymentPlan({ values, fieldsByKey, setValue, renderRow, langs = ['ka'], optionText, inputClass, monthlyRoute, setMonthlyRoute, signingChosen = true, onSigningChosen = () => {},
  opening = null, openingQuestionsList = [], onChangeOpening = () => {} }) {
  const remote = values.remote_transaction === true
  // a new lease starts with the question OPEN — nothing is lit until the agent answers it
  const route = signingChosen ? signingRouteOf(values) : null
  const sameOk = monthlySameAvailable(values)
  // ⚠️ NO SILENT MONTHLY DEFAULT (2026-09-17, end-to-end run): when the agent took the cash at signing,
  //    "the same way" is impossible and the form quietly lit "transfer to the owner" from the template
  //    default and demanded an IBAN. A saved contract (null) reads its stored values; a new one whose
  //    "same" no longer applies is left OPEN until the agent answers.
  const monthly = monthlyRoute == null ? monthlyRouteOf(values) : monthlyRoute === 'same' && !sameOk ? null : monthlyRoute
  const run = steps => steps.forEach(([k, v]) => setValue(k, v))
  const row = (key, over = null) => (fieldsByKey[key] ? renderRow(over ? { ...fieldsByKey[key], ...over } : fieldsByKey[key]) : null)
  // 🌐 the custom rule in the contract's OTHER languages, right under it — only the languages this contract
  // uses, and only once the text itself is written (owner 2026-09-23: "create under it, only used languages")
  const translations = key => ['en', 'ru'].filter(l => (langs || []).includes(l) && String(values?.[key] || '').trim())
    .map(l => <div key={`${key}_${l}`}>{row(`${key}_${l}`)}</div>)

  // "the same way as at signing" follows the signing answer every time it changes
  useEffect(() => {
    if (!signingChosen || monthly !== 'same' || !sameOk) return
    const want = monthlySteps('same', values).filter(([k, v]) => String(values[k] ?? '') !== String(v ?? ''))
    if (want.length) run(want)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // ⚠️ signingChosen too: the opening sets the METHOD before the recipient is chosen, so choosing the
  //    recipient changes no value this list watched and "the same way" never followed (2026-09-19)
  }, [monthly, sameOk, signingChosen, values.payment_method, values.payment_account, values.payment_bank, values.payment_account_holder,
    values.initial_payment_swift, values.initial_bank_charges, values.recurring_payment_method, values.owner_payment_iban, values.recurring_bank_charges,
    values.initial_completion_rule, values.initial_processing_days, values.initial_custom_completion_rule])

  // 💸 a "shared fees" answer for a stage that no longer allows any transfer is reset with its note (reviews
  //    2026-10-09): every route change and every unticked transfer under "several ways" lands here, so a hidden,
  //    half-written note can never block the save or reach the document
  useEffect(() => {
    const steps = inactiveFeeSteps(values)
    if (steps.length) run(steps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.payment_method, values.recurring_payment_method, values.recurring_payment_methods, values.initial_bank_charges, values.recurring_bank_charges])

  const chooseSigning = id => { onSigningChosen(); run(signingSteps(id, values, { remote })) }
  const chooseMonthly = id => {
    setMonthlyRoute(id)
    if (id !== 'same') run(monthlySteps(id, values))
  }
  const kindSelect = key => (
    <label className="block space-y-1" data-transfer-kind={key}>
      <span className="text-[11px] text-[#14130F]/60">{t('desk_contract_pay_transfer_kind')}</span>
      <select className={`${inputClass} w-full`} value={TRANSFER_KINDS.includes(values[key]) ? values[key] : 'local_bank_transfer'}
        onChange={e => setValue(key, e.target.value)}>
        {TRANSFER_KINDS.map(k => {
          const o = (fieldsByKey.payment_method?.options || []).find(x => x.value === k)
          return <option key={k} value={k}>{o ? optionText(o) : k}</option>
        })}
      </select>
    </label>
  )
  const transfer = r => ['owner_transfer', 'agent_transfer', 'agency_transfer'].includes(r)
  // 🚪 once the opening is answered, the method is fixed there: the form asks only WHO receives it
  const fixedRoutes = opening ? routesForMethod(values.payment_method) : null
  const routeShown = id => signingRouteAllowed(id, remote) && (!fixedRoutes || fixedRoutes.includes(id))
  const monthlyIds = ['same', 'any', 'multiple', 'owner_cash', 'owner_transfer', 'owner_card', 'other']
    .filter(id => id !== 'same' || (sameOk && opening?.recurring !== 'specified_in_contract'))
  const sameLocked = opening?.recurring === 'same_as_initial' && sameOk

  return (
    <div className="space-y-5 min-w-0" data-payment-plan>
      {/* B — who takes the money paid at signing, and how */}
      {opening && <RentalOpeningSummary answers={opening} questions={openingQuestionsList} onChange={onChangeOpening} only={['method', 'recurring']} />}
      <section className="space-y-2.5" data-payment-signing={route || 'unchosen'} data-field="payment_signing_route">
        <div className="text-[13px] font-medium">{t(opening ? 'desk_contract_pay_recipient_title' : 'desk_contract_pay_signing_title')}</div>
        <div className="flex flex-col gap-1.5" role="radiogroup">
          {SIGNING_ROUTES.filter(r => routeShown(r.id)).map(r => (
            <button key={r.id} type="button" role="radio" aria-checked={route === r.id} data-signing-route={r.id}
              className={`${CHOICE} ${route === r.id ? ON : OFF}`} onClick={() => chooseSigning(r.id)}>
              {t(`desk_contract_pay_route_${r.id}`)}
            </button>
          ))}
        </div>
        <div className="space-y-3 border-l-2 border-[#14130F]/10 pl-3">
          {(route === 'agent_cash' || route === 'agent_transfer') && row('initial_receiving_agent')}
          {route === 'agency_transfer' && row('payment_recipient', { label_ka: t('desk_contract_pay_account_name'), label_en: t('desk_contract_pay_account_name'), help_ka: '', help_en: '' })}
          {transfer(route) && !opening && kindSelect('payment_method')}
          {transfer(route) && row('payment_account')}
          {transfer(route) && row('payment_reference')}
          {transfer(route) && row('initial_bank_charges')}
          {transfer(route) && values.initial_bank_charges === 'shared' && row('initial_bank_charges_note')}
          {transfer(route) && values.initial_bank_charges === 'shared' && translations('initial_bank_charges_note')}
          {route === 'owner_card' && row('payment_app')}
          {route === 'other' && row('payment_recipient', { label_ka: t('desk_contract_pay_other_who'), label_en: t('desk_contract_pay_other_who'), help_ka: '', help_en: '', required: true })}
          {route === 'other' && row('initial_custom_completion_rule', { label_ka: t('desk_contract_pay_other_how'), label_en: t('desk_contract_pay_other_how'), help_ka: t('desk_contract_pay_other_how_help'), help_en: t('desk_contract_pay_other_how_help') })}
          {route === 'other' && translations('initial_custom_completion_rule')}
        </div>
      </section>

      {/* C — monthly rent afterwards: asked once B is answered, because "the same way" means B */}
      {signingChosen && <section className="space-y-2.5" data-payment-monthly={monthly} data-field="payment_monthly_route">
        <div className="text-[13px] font-medium">{t('desk_contract_pay_monthly_title')}</div>
        {sameLocked && <div className="text-[12px] text-[#14130F]/70" data-monthly-same-note>{t('desk_rental_open_sum_same_long')}</div>}
        {opening?.recurring === 'same_as_initial' && !sameOk && <div className="text-[12px] text-[#8A4B2A]">{t('desk_rental_open_same_unavailable')}</div>}
        {!sameLocked && <div className="flex flex-col gap-1.5" role="radiogroup">
          {monthlyIds.map(id => (
            <button key={id} type="button" role="radio" aria-checked={monthly === id} data-monthly-route={id}
              className={`${CHOICE} ${monthly === id ? ON : OFF}`} onClick={() => chooseMonthly(id)}>
              {t(`desk_contract_pay_monthly_${id}`)}
            </button>
          ))}
        </div>}
        <div className="space-y-3 border-l-2 border-[#14130F]/10 pl-3">
          {monthly === 'multiple' && row('recurring_payment_methods')}
          {/* a transfer among the ticked ways needs the account; "any way" asks for it optionally */}
          {(monthly === 'any' || (monthly === 'multiple' && (values.recurring_payment_methods || []).some(m => m !== 'cash' && m !== 'card_or_electronic_payment'))) && row('owner_payment_iban', monthly === 'any' ? { required: false } : undefined)}
          {/* "the same way" by bank transfer still pays the OWNER's account, which is its own field — it is never
              copied from the signing recipient (an agent may have taken that money). Without this row the form
              demanded an IBAN it never drew (2026-09-19). */}
          {/* 💸 "any way" and "several ways" with a transfer ticked allow transfers, so their fee terms print — and are asked
              here (reviews 2026-10-09: the choice was invisible while the document used it) */}
          {(monthly === 'any' || monthly === 'multiple') && transfersAllowed('recurring', values) && row('recurring_bank_charges')}
          {(monthly === 'any' || monthly === 'multiple') && transfersAllowed('recurring', values) && values.recurring_bank_charges === 'shared' && row('recurring_bank_charges_note')}
          {(monthly === 'any' || monthly === 'multiple') && transfersAllowed('recurring', values) && values.recurring_bank_charges === 'shared' && translations('recurring_bank_charges_note')}
          {monthly === 'same' && TRANSFER_KINDS.includes(values.recurring_payment_method) && row('owner_payment_iban')}
          {monthly === 'same' && TRANSFER_KINDS.includes(values.recurring_payment_method) && row('recurring_bank_charges')}
          {monthly === 'same' && TRANSFER_KINDS.includes(values.recurring_payment_method) && values.recurring_bank_charges === 'shared' && row('recurring_bank_charges_note')}
          {monthly === 'same' && TRANSFER_KINDS.includes(values.recurring_payment_method) && values.recurring_bank_charges === 'shared' && translations('recurring_bank_charges_note')}
          {monthly === 'owner_transfer' && kindSelect('recurring_payment_method')}
          {monthly === 'owner_transfer' && row('owner_payment_iban')}
          {monthly === 'owner_transfer' && row('recurring_bank_charges')}
          {monthly === 'owner_transfer' && values.recurring_bank_charges === 'shared' && row('recurring_bank_charges_note')}
          {monthly === 'owner_transfer' && values.recurring_bank_charges === 'shared' && translations('recurring_bank_charges_note')}
          {monthly === 'other' && row('recurring_custom_completion_rule', { label_ka: t('desk_contract_pay_monthly_other_how'), label_en: t('desk_contract_pay_monthly_other_how'), help_ka: t('desk_contract_pay_other_how_help'), help_en: t('desk_contract_pay_other_how_help') })}
          {monthly === 'other' && translations('recurring_custom_completion_rule')}
        </div>
      </section>}
    </div>
  )
}
